import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Button, 
  Card, 
  CardContent, 
  Grid, 
  Typography, 
  Container, 
  Box, 
  IconButton, 
  CardMedia,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { CleaningEquipment } from 'types/service';
import { getCleaningEquipment, deleteCleaningEquipment } from 'services/serviceService';

const CleaningEquipmentList: React.FC = () => {
  const [equipment, setEquipment] = useState<CleaningEquipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEquipment = async () => {
      try {
        const data = await getCleaningEquipment();
        setEquipment(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch cleaning equipment');
        setLoading(false);
      }
    };

    fetchEquipment();
  }, []);

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this equipment?')) {
      try {
        await deleteCleaningEquipment(id);
        setEquipment(equipment.filter(item => item.equipment_id !== id));
      } catch (err) {
        setError('Failed to delete equipment');
      }
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
      <CircularProgress />
    </Box>
  );
  
  if (error) return (
    <Alert severity="error" sx={{ my: 2 }}>
      {error}
    </Alert>
  );

  return (
    <div>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        
        <Typography variant="h4" gutterBottom> Cleaning Equipment </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          component={Link}
          to="/cleaning-equipment/new"
        >
          Add New Equipment
        </Button>
      </Box>

      <Grid container spacing={3}>
        {equipment.map((item) => (
          <Grid item key={item.equipment_id} xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" gap={2}>
                  {item.image_url && (
                    <CardMedia
                      component="img"
                      image={item.image_url}
                      alt={item.name}
                      sx={{
                        width: 100,
                        height: 100,
                        objectFit: 'cover',
                        borderRadius: 1,
                        alignSelf: 'flex-start'
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1, position: 'relative', minHeight: 100 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="h6">{item.name}</Typography>
                      <Box>
                        <IconButton
                          size="small"
                          color="primary"
                          component={Link}
                          to={`/cleaning-equipment/edit/${item.equipment_id}`}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(item.equipment_id)}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Chip 
                        label={item.status === 1 ? 'Active' : 'Inactive'}
                        color={item.status === 1 ? 'success' : 'default'}
                        size="small"
                        variant={item.status === 1 ? 'filled' : 'outlined'}
                        sx={{ 
                          fontWeight: 'medium',
                          borderWidth: '1.5px',
                          backgroundColor: item.status === 1 ? '' : 'white',
                          '& .MuiChip-label': {
                            px: 1,
                            fontSize: '0.75rem',
                            color: item.status === 1 ? 'white' : 'inherit'
                          },
                          '&.MuiChip-outlined': {
                            borderColor: 'rgba(0, 0, 0, 0.23)'
                          }
                        }}
                      />
                    </Box>
                    <Typography 
                      variant="body2" 
                      color="textSecondary" 
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        mb: 1
                      }}
                    >
                      {item.description || 'No description provided'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default CleaningEquipmentList;
